import React, { useState, useEffect } from "react";
import themeClasses from "../../themeClasses";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import CountrySelect from "./CountrySelect";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import api from "../../api/axios";

export default function AuthModal({
  mode = "login",
  setMode,
  onClose,
  onLoginSuccess,
  currentTheme = "light",
}) {
  const [currentMode, setCurrentMode] = useState(mode);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const initialFormData = {
    country: "BD",
    name: "",
    lastName: "",
    company: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reset form and mode
  useEffect(() => {
    setCurrentMode(mode);
    setMessage("");
    setFormData(initialFormData);
  }, [mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      if (currentMode === "register") {
        // === VALIDATION ===
        if (
          !formData.name ||
          !formData.lastName ||
          !formData.company ||
          !formData.email ||
          !formData.password ||
          !formData.confirmPassword ||
          !formData.phone ||
          !formData.country
        ) {
          setMessage("Please fill all required fields.");
          setLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setMessage("Passwords do not match.");
          setLoading(false);
          return;
        }

        const phoneNumber = formData.phone.startsWith("+")
          ? formData.phone
          : `+${formData.phone}`;
        if (!/^\+\d{10,15}$/.test(phoneNumber)) {
          setMessage("Phone number is invalid.");
          setLoading(false);
          return;
        }

        // === REGISTER API ===
        const payload = {
          name: `${formData.name?.trim()} ${formData.lastName?.trim()}`,
          company: formData.company?.trim(),
          email: formData.email?.trim(),
          password: formData.password,
          phone: phoneNumber,
          country: formData.country,
          user_type: "Buyer",
          privacy_and_policies: true,
        };

        console.log("SIGNUP PAYLOAD:", payload);

        const res = await api.post("/api/auth/signup/", payload);

        if (res.status >= 200 && res.status < 300) {
          setMessage("Registration successful. Logging in...");

          // Auto-login after registration
          const loginRes = await api.post("/api/subscription/auth/token/", {
            email: formData.email,
            password: formData.password,
          });

          localStorage.setItem("access_token", loginRes.data.access);
          localStorage.setItem("refresh_token", loginRes.data.refresh);

          const user = {
            id: loginRes.data.user?.id || formData.email,
            email: formData.email,
            name: loginRes.data.user?.name || "",
          };

          // Pass user back to App.js
          onLoginSuccess?.(user);
        } else {
          setMessage("Registration failed. Please try again.");
        }
      } else {
        // === LOGIN API ===
        if (!formData.email || !formData.password) {
          setMessage("Please fill all required fields.");
          setLoading(false);
          return;
        }

        const res = await api.post("/api/subscription/auth/token/", {
          email: formData.email,
          password: formData.password,
        });

        localStorage.setItem("access_token", res.data.access);
        localStorage.setItem("refresh_token", res.data.refresh);

        // Create user object to pass back
        const user = {
          id: res.data.user?.id || formData.email, // fallback to email if no id
          email: formData.email,
          name: res.data.user?.name || "",
        };

        // Pass user back to App.js
        onLoginSuccess?.(user);
      }
    } catch (err) {
      console.error("FULL API ERROR:", err);

      if (err.response) {
        console.error("RESPONSE DATA:", err.response.data);
        const data = err.response.data;
        if (typeof data === "string") setMessage(data);
        else if (data?.message) setMessage(data.message);
        else if (data?.detail) setMessage(data.detail);
        else if (data?.name) setMessage(`Name: ${data.name}`);
        else setMessage(JSON.stringify(data));
      } else if (err.request) {
        setMessage("Server not reachable. Please try again later.");
      } else {
        setMessage(err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-50 ${themeClasses[currentTheme].modalOverlay}`}
      onClick={onClose}
    >
      <div
        className={`p-6 rounded-xl border shadow-lg w-[500px] relative ${themeClasses[currentTheme].modalBox}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="absolute top-1 right-1 text-xl" onClick={onClose}>
          <IoIosCloseCircleOutline size={28} />
        </button>

        <h2 className="text-2xl font-semibold mb-5 text-center">
          {currentMode === "login" ? "Login" : "Register"}
        </h2>

        <form onSubmit={handleSubmit}>
          {currentMode === "register" && (
            <>
              <CountrySelect
                value={formData.country}
                onChange={(val) => setFormData({ ...formData, country: val })}
                className={`w-full p-2 border rounded ${themeClasses[currentTheme].modalInput}`}
              />

              <div className="flex gap-2 mt-3">
                <input
                  name="name"
                  placeholder="First Name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${themeClasses[currentTheme].modalInput}`}
                />
                <input
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${themeClasses[currentTheme].modalInput}`}
                />
              </div>

              <div className="flex gap-2 mt-3">
                <input
                  name="company"
                  placeholder="Company"
                  value={formData.company}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${themeClasses[currentTheme].modalInput}`}
                />
              </div>

              <div className="flex gap-2 mt-3">
                <PhoneInput
                  country={formData.country?.toLowerCase() || "bd"}
                  value={formData.phone}
                  onChange={(val) => setFormData({ ...formData, phone: val })}
                  inputClass="w-full"
                  containerClass="w-full"
                  placeholder="Enter phone number"
                />
              </div>
            </>
          )}

          <input
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full p-2 border rounded mt-3 ${themeClasses[currentTheme].modalInput}`}
          />

          <div className="relative mt-3">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${themeClasses[currentTheme].modalInput}`}
            />
            <span
              className="absolute right-3 top-3 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          {currentMode === "register" && (
            <div className="relative mt-3">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full p-2 border rounded ${themeClasses[currentTheme].modalInput}`}
              />
              <span
                className="absolute right-3 top-3 cursor-pointer"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          )}

          {message && (
            <div className="text-red-500 text-sm mt-2">{message}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full mt-4 py-2 rounded-full border ${themeClasses[currentTheme].btnSubmit}`}
          >
            {loading
              ? "Please wait..."
              : currentMode === "login"
              ? "Login"
              : "Register"}
          </button>
        </form>

        <p className="text-center text-sm mt-4">
          {currentMode === "login" ? (
            <>
              Don’t have an account?{" "}
              <span
                className="cursor-pointer underline"
                onClick={() => setCurrentMode("register")}
              >
                Register
              </span>
            </>
          ) : (
            <>
              Already have an account?
              <span
                className="cursor-pointer underline"
                onClick={() => setCurrentMode("login")}
              >
                Login
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
